import _ from 'lodash';
import $ from 'jquery';
import moment from 'moment';
import Vue from 'vue';
import {mapActions, mapGetters, mapState} from 'vuex';
import {required, maxLength, between, numeric} from 'vuelidate/lib/validators';
import {AxiosError, AxiosResponse} from 'axios';
import {EmployeeProfileModuleConstant, FreeProgramConstant, PageDriverConstant, RegularExpressionConstant, SectionConstant, UrlModuleConstant} from '../../lib/constants';
import FreePrograms from './freePrograms.html';
import MomentFilter from '../../lib/filters/momentFilter';
import {personService} from '../../lib/services/personService';
import {planService} from '../../lib/services/planService';
import EcCommon from '../../../types/ec/ecCommon';
import LTFramework from '../../../types/lt/ltFramework';

interface IComponentData {
	freePrograms: EcCommon.IFreeProgram[];
	freeProgramRequest: EcCommon.IFreeProgramRequest;
	initializing: boolean;
	isCreateMode: boolean;
	isUpdateMode: boolean;
	isViewMode: boolean;
	formSubmitted: boolean;
	formErrorMessage: string;
	personService: EcCommon.IPersonService;
	planService: EcCommon.IPlanService;
}

export default Vue.component('ecFreePrograms', {
	template: FreePrograms,
	data(): IComponentData {
		return {
			freePrograms: [],
			freeProgramRequest: {
				isActive: true,
			} as EcCommon.IFreeProgramRequest,
			initializing: true,
			isCreateMode: false,
			isUpdateMode: false,
			isViewMode: false,
			formSubmitted: false,
			formErrorMessage: '',
			personService,
			planService,
		};
	},
	computed: {
		...mapGetters(EmployeeProfileModuleConstant.name, {
			getIsEmployeeProfileValid: EmployeeProfileModuleConstant.getter.getIsValid
		}),
		...mapGetters(UrlModuleConstant.name, [UrlModuleConstant.getter.getGlobalPageDriverUrl]),
		...mapState(EmployeeProfileModuleConstant.name, [EmployeeProfileModuleConstant.state.employeeProfile]),
		activeFreePrograms(): EcCommon.IFreeProgram[] {
			const filteredActive = _.filter(this.freePrograms, (freeProgram) => {
				return freeProgram.isActive;
			});

			return _.orderBy(filteredActive, ['featured', 'priority', 'programName'], ['desc', 'asc', 'asc']);
		},
		inactiveFreePrograms(): EcCommon.IFreeProgram[] {
			const filteredInactive = _.filter(this.freePrograms, (freeProgram) => {
				return !freeProgram.isActive;
			});

			return _.orderBy(filteredInactive, ['updatedDate'], ['desc']);
		},
		loading(): boolean {
			return this.initializing || this.personService.loading || this.planService.loading;
		},
		errorMessage(): string {
			let errorMessage = '';
			if (this.personService.errorMessage) {
				errorMessage = this.personService.errorMessage;
			}

			if (this.planService.errorMessage) {
				errorMessage = this.planService.errorMessage;
			}

			return errorMessage;
		},
	},
	methods: {
		showFreePrograms(): Promise<void> {
			return personService.getPersonRoles(FreeProgramConstant.ApplicationId, FreeProgramConstant.RoleId)
				.then((response: AxiosResponse<EcCommon.IEmployeeUser[]>) => {
					const employee = _.find(response.data, ['EmployeeId', (this.employeeProfile.employeeDetails as LTFramework.IEmployeeDetails).employeeId]);
					if (employee) {
						const role = _.find(employee.Roles, (employeeRole) => {
							return employeeRole.ApplicationId.toUpperCase() === FreeProgramConstant.ApplicationId && employeeRole.RoleId.toUpperCase() === FreeProgramConstant.RoleId;
						});
						if (role) {
							return this.getFreePrograms();
						} else {
							window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.home));
							return Promise.resolve();
						}
					} else {
						window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.home));
						return Promise.resolve();
					}
				});
		},
		getFreePrograms(): Promise<void> {
			return planService.getFreePrograms(this.employeeProfile.partyId)
				.then((response: AxiosResponse<EcCommon.IFreeProgram[]>) => {
					this.freePrograms = response.data;
				});
		},
		submitForm(submitEvent: Event): Promise<void> {
			this.formSubmitted = true;
			if (this.$v.$invalid) {
				$('#createModal').scrollTop(0);

				return Promise.resolve();
			}

			if (!(this.isUpdateMode || this.isViewMode)) {
				return this.create();
			} else {
				return this.update();
			}
		},
		create(): Promise<void> {
			return planService.createFreeProgram(this.employeeProfile.partyId, this.freeProgramRequest)
				.then((response: AxiosResponse<EcCommon.IFreeProgram>) => {
					this.freePrograms.push(response.data);
					this.formSubmitted = false;

					$('#createModal').modal('hide');
				}, (error: AxiosError) => {
					$('#createModal').scrollTop(0);
					this.formErrorMessage = 'Oops! Something went wrong! Please try again later.';
				});
		},
		update(): Promise<void> {
			return planService.updateFreeProgram(this.employeeProfile.partyId, this.freeProgramRequest)
				.then((response: AxiosResponse<EcCommon.IFreeProgram>) => {
					const updateIndex = _.findIndex(this.freePrograms, ['freeProgramId', response.data.freeProgramId]);
					this.$set(this.freePrograms, updateIndex, response.data);
					this.formSubmitted = false;

					$('#createModal').modal('hide');
				}, (error: AxiosError) => {
					$('#createModal').scrollTop(0);
					this.formErrorMessage = 'Oops! Something went wrong! Please try again later.';
				});
		},
		deactivate(): Promise<void> {
			return planService.updateFreeProgram(this.employeeProfile.partyId, this.freeProgramRequest)
				.then((response: AxiosResponse<EcCommon.IFreeProgram>) => {
					const updateIndex = _.findIndex(this.freePrograms, ['freeProgramId', response.data.freeProgramId]);
					this.$set(this.freePrograms, updateIndex, response.data);

					$('#deactivateModal').modal('hide');
				}, (error: AxiosError) => {
					this.formErrorMessage = 'Oops! Something went wrong! Please try again later.';
				});
		},
		showCreateModal(): void {
			this.$v.$reset();

			this.isCreateMode = true;
			this.isUpdateMode = false;
			this.isViewMode = false;
			this.formSubmitted = false;
			this.formErrorMessage = '';

			this.freeProgramRequest = {
				isActive: true,
			} as EcCommon.IFreeProgramRequest;

			$('#createModal').modal('show');
		},
		showUpdateModal(programToUpdate: EcCommon.IFreeProgram): void {
			this.$v.$reset();

			this.isCreateMode = false;
			this.isUpdateMode = true;
			this.isViewMode = false;
			this.formSubmitted = false;
			this.formErrorMessage = '';

			this.freeProgramRequest = _.cloneDeep(programToUpdate);

			$('#createModal').modal('show');
		},
		showDeactivateModal(programToDeactivate: EcCommon.IFreeProgram): void {
			this.$v.$reset();

			this.isCreateMode = false;
			this.isUpdateMode = false;
			this.isViewMode = false;
			this.formSubmitted = false;
			this.formErrorMessage = '';

			this.freeProgramRequest = _.cloneDeep(programToDeactivate);
			this.freeProgramRequest.isActive = false;
			this.freeProgramRequest.endDate = moment();

			$('#deactivateModal').modal('show');
		},
		showViewModal(programToView: EcCommon.IFreeProgram): void {
			this.$v.$reset();

			this.isCreateMode = false;
			this.isUpdateMode = false;
			this.isViewMode = true;
			this.formSubmitted = false;
			this.formErrorMessage = '';

			this.freeProgramRequest = programToView;

			$('#createModal').modal('show');
		},
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
	},
	created(): void {
		if (window.__PRERENDER_INJECTED || RegularExpressionConstant.authorRegex.test(window.location.href)) {
			return;
		}

		Promise.all([this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore()]).then(() => {
			if (!this.getIsEmployeeProfileValid) {
				window.location.assign(this.getGlobalPageDriverUrl(SectionConstant.global, PageDriverConstant.loginResource));
			} else {
				return this.showFreePrograms();
			}
		}).finally(() => {
			this.initializing = false;
		});
	},
	validations: {
		freeProgramRequest: {
			programId: {
				numeric,
				required,
				maxLength: maxLength(10),
			},
			programImage: {
				required,
				maxLength: maxLength(2000),
			},
			programName: {
				required,
				maxLength: maxLength(50),
			},
			programDescription: {
				required,
				maxLength: maxLength(5000),
			},
			priority: {
				between: between(1, 999),
				numeric,
				required,
			},
			frequency: {
				required,
				maxLength: maxLength(50),
			},
			duration: {
				required,
				maxLength: maxLength(50),
			},
			level: {
				required,
				maxLength: maxLength(50),
			},
			goal: {
				required,
				maxLength: maxLength(50),
			},
		},
	},
	filters: {
		momentFilter: MomentFilter,
	},
});
